"use client";

import { Button } from "@/components/new/button";
import { Label } from "@/components/new/label";
import { Input } from "@/components/ui";
import { PlusIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface CustomTagSelectProps<T extends { [key in keyof T]: string[] }> {
	name: keyof T;
	setValue: (name: keyof T, value: string[]) => void;
	getValues: (name: keyof T) => string[];
}

const CustomTagSelect = <T extends { [key in keyof T]: string[] }>({
	name,
	setValue,
	getValues,
}: CustomTagSelectProps<T>) => {
	const [query, setQuery] = useState<string>("");
	const [selectedTags, setSelectedTags] = useState<string[]>([]);

	const removeTag = (value: string) => {
		const updatedTags = selectedTags.filter((tag) => tag !== value);
		setSelectedTags(updatedTags);
		setValue(name, updatedTags);
		setQuery("");
	};

	const handleAddNewTag = async () => {
		if (!query || query.trim() === "") {
			return;
		}

		const tagToAdd = query.trim();

		const tagExists = selectedTags.some((tag) => tag.toLowerCase() === tagToAdd.toLowerCase());

		if (tagExists) {
			setQuery("");
			return;
		}

		if (selectedTags.length >= 5) {
			return;
		}

		const updatedTags = [...selectedTags, tagToAdd];
		setSelectedTags(updatedTags);
		setValue(name, updatedTags);
		setQuery("");
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAddNewTag();
		}
	};

	useEffect(() => {
		const existingTags = (getValues(name) as string[]) || [];
		setSelectedTags(existingTags);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="flex flex-col gap-2">
			<Label>Tags</Label>
			<div className="flex flex-wrap gap-2">
				{selectedTags?.map((tag) => (
					<Button
						key={tag}
						onClick={() => removeTag(tag)}
						className="flex items-center justify-between gap-2 rounded-lg border border-gray-200"
						variant={"secondary"}
					>
						<span className="text-sm">{tag}</span>
						<div>
							<XIcon className="h-4 w-4" />
						</div>
					</Button>
				))}
			</div>

			<div className="group relative">
				<Input
					type="text"
					value={query}
					placeholder="Enter a custom tag"
					classNames={{
						inputContainer: "py-1 px-2 w-full rounded-md focus:outline-none",
						label: "mb-0",
						container: "w-full",
					}}
					onChange={(e) => {
						setQuery(e.target.value);
					}}
					onKeyDown={handleKeyDown}
					disabled={selectedTags.length >= 5}
				/>

				{query.trim() !== "" && (
					<div className="absolute left-0 top-12 z-10 w-full rounded-md bg-white p-4 text-left shadow">
						<button
							type="button"
							onClick={handleAddNewTag}
							className="flex w-full items-center justify-start gap-2 rounded-md p-2 text-sm text-brand-primary hover:bg-blue-50"
						>
							<PlusIcon className="h-4 w-4" />
							<span>Add &quot;{query.trim()}&quot; as new custom tag</span>
						</button>
					</div>
				)}
			</div>

			<div className="flex justify-between p-2">
				<span className="text-xs text-gray-500">{selectedTags.length}/5 tags</span>
				{selectedTags.length > 0 ? (
					<button
						className="text-xs text-red-500"
						onClick={() => {
							setSelectedTags([]);
							setValue(name, []);
						}}
					>
						Clear All
					</button>
				) : null}
			</div>
		</div>
	);
};

export default CustomTagSelect;
